import { FC, useCallback, useMemo } from 'react';
import { Form, Formik } from 'formik';
import { toFormikValidationSchema } from 'zod-formik-adapter';
import z from 'zod';
import SaveIcon from '@mui/icons-material/Save';
import { Theme, useMediaQuery } from '@mui/material';

import { Service } from 'domain/entities/Service';
import { useCaseUpdateService } from 'application/services/useCases/useCaseUpdateService';
import { useTranslationPrefix } from 'infrastructure/translations/i18n';
import { Modal } from 'targets/web/components';
import { FormikTextField } from 'targets/web/modules/dashboard/components';

export const EditServiceNotesFormSchema = z.object({
  notes: z.string().max(199),
});

export type EditServiceNotesFormData = z.infer<typeof EditServiceNotesFormSchema>;

export interface EditServiceNotesDialogProps {
  onSubmit: (values: EditServiceNotesFormData) => void | Promise<void>;
  onClose: () => void;
  open: boolean;
  service: Pick<Service, 'id' | 'notes'>;
}

export const EditServiceNotesDialog: FC<EditServiceNotesDialogProps> = ({
  onSubmit,
  onClose,
  open,
  service,
}) => {
  const t = useTranslationPrefix('jobs.edit_notes_dialog');
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));

  const { updateService } = useCaseUpdateService();

  const initialValues: EditServiceNotesFormData = useMemo(
    () => ({
      notes: service?.notes ?? '',
    }),
    [service?.notes],
  );

  const handleSubmit = useCallback(
    async (values: EditServiceNotesFormData) => {
      await updateService({
        serviceId: service.id,
        data: {
          notes: values.notes,
        },
      });

      onSubmit(values);
      onClose();
    },
    [onClose, onSubmit, service.id, updateService],
  );

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={handleSubmit}
      enableReinitialize
      validateOnMount
      validationSchema={toFormikValidationSchema(EditServiceNotesFormSchema)}
    >
      {({ isValid, isSubmitting, submitForm }) => (
        <Form>
          <Modal
            disableBackdropClick
            onClose={onClose}
            open={open}
            title={t('title')}
            maxWidth="sm"
            fullWidth
            fullScreen={isMobile}
            onClick={(e) => e.stopPropagation()}
            content={
              <FormikTextField
                name="notes"
                label={t('form.notes_label')}
                placeholder={t('form.notes_placeholder')}
                multiline
                rows={4}
                fullWidth
              />
            }
            actions={{
              primary: {
                size: 'large',
                type: 'submit',
                startIcon: <SaveIcon />,
                text: t('buttons.save'),
                disabled: !isValid || isSubmitting,
                onClick: submitForm,
                loading: isSubmitting,
              },
              secondary: {
                size: 'large',
                text: t('buttons.cancel'),
              },
            }}
          />
        </Form>
      )}
    </Formik>
  );
};
